interface AbTestEntry {
    id: string | null;
    event: string | null;
    label: string;
    source: 'window' | 'abtasty' | 'storage' | 'dom';
    active: boolean;
    activeAt?: number;
}

interface AbTestSnapshot {
    entries: AbTestEntry[];
    activeCount: number;
}

const REGISTRY_EVENT = 'ab-registry-updated';
let initialized = false;
const entries = new Map<string, AbTestEntry>();

const DEFAULT_AB_TESTS: AbTestEntry[] = [
    {
        id: 'ab-ibt-rdv-countdown',
        event: 'ab-ibt-rdv-countdown',
        label: 'Tunnel IBT : compte à rebours RDV',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-ibt-no-auto-advance',
        event: 'ab-ibt-no-auto-advance',
        label: 'Tunnel IBT : pas de passage automatique',
        source: 'window',
        active: false,
    },
    {
        id: '86c5mdwbh',
        event: 'expressAppointementToQuotation',
        label: 'Quotation form instead of appointment',
        source: 'window',
        active: false,
    },
    {
        id: '86c4u3xqb',
        event: 'show-modal-user-exit',
        label: 'Exit intent modal (user exit)',
        source: 'window',
        active: false,
    },
    {
        id: '86c4u3xqb',
        event: 'show-modal-after-x-pages-viewed',
        label: 'Exit intent modal (x pages)',
        source: 'window',
        active: false,
    },
    {
        id: '86c4u3xqb',
        event: 'show-modal-after-x-seconds-on-a-page',
        label: 'Exit intent modal (x seconds)',
        source: 'window',
        active: false,
    },
    {
        id: '86c5m05un',
        event: 'show-quick-links-product',
        label: 'Quick links product',
        source: 'window',
        active: false,
    },
    {
        id: '86c5m05un',
        event: 'show-quick-links-product-v3',
        label: 'Quick links product V3',
        source: 'window',
        active: false,
    },
    {
        id: 'choix-preliminaire',
        event: 'ab-init-quotation-multiple-choice',
        label: 'Quotation init multiple choice',
        source: 'window',
        active: false,
    },
    {
        id: 'validation-checkmark',
        event: 'ab-quotation-laststep-validation',
        label: 'Quotation last step validation',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-recap-reviews',
        event: 'ab-recap-reviews',
        label: 'Recap reviews',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-appointment-delete-rdv-step',
        event: 'ab-appointment-delete-rdv-step',
        label: 'Delete RDV step',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-move-location',
        event: 'ab-move-location',
        label: 'Move location',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-appointment-mobile-proressbar',
        event: 'ab-appointment-mobile-proressbar',
        label: 'Appointment mobile progress bar',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-quotation-progressbar',
        event: 'ab-quotation-progressbar',
        label: 'Quotation progress bar',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-quick-meethours',
        event: 'localStorage',
        label: 'Quick meethours',
        source: 'storage',
        active: false,
    },
    {
        id: 'ab-phone-country-prefix',
        event: 'ab-phone-country-prefix',
        label: 'Phone country prefix selector (appointment)',
        source: 'window',
        active: false,
    },
    {
        id: 'show-header-wording-and-reviews',
        event: 'show-header-wording-and-reviews',
        label: 'Show header wording and reviews in quotation v1',
        source: 'window',
        active: false,
    },
    {
        id: 'quotation-toggle-detail',
        event: 'quotation-toggle-detail',
        label: 'Quotation coordonates toggle detail',
        source: 'window',
        active: false,
    },
    {
        id: 'optin-and-consent-quotation',
        event: 'optin-and-consent-quotation',
        label: 'Optin and consent quotation v1',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-quotation-v1-material',
        event: 'ab-quotation-v1-material',
        label: 'Quotation v1 material step as V2/V3',
        source: 'window',
        active: false,
    },
    {
        id: 'recap-quotation-v1',
        event: 'recap-quotation-v1',
        label: 'Recap quotation v1 as quotation v2/3',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-quotation-cp-step',
        event: 'ab-quotation-cp-step',
        label: 'Skip CP step',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-skip-project-type-step',
        event: 'ab-skip-project-type-step',
        label: 'Skip project type step in quotation form v1',
        source: 'window',
        active: false,
    },
    {
        id: 'ab-quotation-sticky-recap',
        event: 'ab-quotation-sticky-recap',
        label: 'Quotation sticky recap header (mobile)',
        source: 'window',
        active: false,
    },
];

const toKey = (entry: Pick<AbTestEntry, 'id' | 'event' | 'source'>): string => {
    return `${entry.id ?? 'n/a'}::${entry.event ?? 'n/a'}::${entry.source}`;
};

const registerEntry = (entry: AbTestEntry): void => {
    const key = toKey(entry);
    if (!entries.has(key)) {
        entries.set(key, { ...entry });
    }
};

const updateEntry = (entry: AbTestEntry): void => {
    const key = toKey(entry);
    entries.set(key, { ...entry });
};

const notify = (): void => {
    const snapshot = getAbRegistrySnapshot();
    window.dispatchEvent(new CustomEvent(REGISTRY_EVENT, { detail: snapshot }));
};

const collectLocalStorageAbTests = (): void => {
    try {
        const prefix = 'ab_test_';
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (!key) continue;
            if (key.startsWith(prefix)) {
                const id = key.slice(prefix.length);
                registerEntry({
                    id,
                    event: 'localStorage',
                    label: `ABTasty: ${id}`,
                    source: 'abtasty',
                    active: true,
                    activeAt: Date.now(),
                });
            }
            if (key.startsWith('ab-quick-meethours')) {
                const existing = Array.from(entries.values()).find(
                    (item) => item.id === 'ab-quick-meethours' && item.source === 'storage'
                );
                if (!existing) {
                    registerEntry({
                        id: 'ab-quick-meethours',
                        event: 'localStorage',
                        label: 'Quick meethours',
                        source: 'storage',
                        active: true,
                        activeAt: Date.now(),
                    });
                } else if (!existing.active) {
                    updateEntry({
                        ...existing,
                        active: true,
                        activeAt: Date.now(),
                    });
                }
            }
        }
    } catch (e) {
        // LocalStorage may be unavailable
    }
};

const collectDomAbTests = (): void => {
    const elements = document.querySelectorAll<HTMLElement>('[data-ab-name]');
    elements.forEach((element) => {
        const id = element.dataset.abName;
        if (!id) return;
        registerEntry({
            id,
            event: 'data-ab-name',
            label: `DOM: ${id}`,
            source: 'dom',
            active: false,
        });
    });
};

export const initAbRegistry = (): void => {
    if (initialized) return;
    initialized = true;
    DEFAULT_AB_TESTS.forEach(registerEntry);
    collectDomAbTests();
    collectLocalStorageAbTests();
    notify();
};

export const activateAbTestByEvent = (eventName: string, detail?: unknown): void => {
    initAbRegistry();
    let updated = false;
    const now = Date.now();

    entries.forEach((entry) => {
        if (entry.event === eventName) {
            updateEntry({ ...entry, active: true, activeAt: now });
            updated = true;
        }
    });

    if (detail && typeof detail === 'object' && 'ABTest' in (detail as Record<string, unknown>)) {
        const id = String((detail as Record<string, unknown>).ABTest);
        activateAbTestById(id, eventName);
        updated = true;
    } else if (typeof detail === 'string') {
        activateAbTestById(detail, eventName);
        updated = true;
    }

    if (updated) {
        notify();
    }
};

export const activateAbTestById = (id: string, eventName?: string): void => {
    initAbRegistry();
    const now = Date.now();
    let updated = false;

    entries.forEach((entry) => {
        if (entry.id === id && (!eventName || entry.event === eventName)) {
            updateEntry({ ...entry, active: true, activeAt: now });
            updated = true;
        }
    });

    if (!updated) {
        registerEntry({
            id,
            event: eventName ?? 'unknown',
            label: id,
            source: 'window',
            active: true,
            activeAt: now,
        });
        updated = true;
    }

    if (updated) {
        notify();
    }
};

export const deactivateAbTestById = (id: string): void => {
    initAbRegistry();
    let updated = false;

    entries.forEach((entry) => {
        if (entry.id === id && entry.active) {
            updateEntry({ ...entry, active: false });
            updated = true;
        }
    });

    if (updated) {
        notify();
    }
};

export const getAbRegistrySnapshot = (): AbTestSnapshot => {
    initAbRegistry();
    const all = Array.from(entries.values());
    const activeCount = all.filter((entry) => entry.active).length;
    const sorted = all.sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return (a.label || a.id || '').localeCompare(b.label || b.id || '');
    });
    return { entries: sorted, activeCount };
};

export const onAbRegistryUpdate = (handler: (snapshot: AbTestSnapshot) => void): void => {
    initAbRegistry();
    window.addEventListener(REGISTRY_EVENT, (event) => {
        const detail = (event as CustomEvent<AbTestSnapshot>).detail;
        handler(detail ?? getAbRegistrySnapshot());
    });
};
